import { addDays } from 'date-fns';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from "../api";
import { OrderStatus } from '../common/constants';
import useWindowDimensions from '../common/hooks/useWindowDimensions';
import { formatPrice } from '../utils';
import Badge from "./Badge";
import StatusBadge from "./StatusBadge";
import styles from './InstallmentsCard.module.css';
import Pagination from './Pagination';
import Table from './Table';
import TextInput from './TextInput';
import TooltipWrapper from './TooltipWrapper';
import Card from './cards/Card';

const InstallmentsCard = ({ modern = false }) => {

    const { width } = useWindowDimensions();

    const [page, setPage] = useState(0);
    const [per, setPer] = useState(width > 540 ? 10 : 5);
    const [q, setQ] = useState("");

    const [installments, setInstallments] = useState([]);
    const [count, setCount] = useState(0);
    const [showPagination, setShowPagination] = useState(true)
    const [loading, setLoading] = useState(false)


    const getInstallmentStatus = (status) => {
        switch (status) {
            case OrderStatus.Pending:
                return {
                    label: "In Attesa",
                    color: "var(--sf-gray)"
                }
            case OrderStatus.Processing:
                return {
                    label: "In elaborazione",
                    color: "var(--sf-yellow)"
                }
            case OrderStatus.Succeeded:
                return {
                    label: "Completato",
                    color: "var(--sf-light-green)"
                }
            case OrderStatus.Failed:
                return {
                    label: "Errore",
                    color: "var(--sf-red)"
                }
            case OrderStatus.Refunded:
                return {
                    label: "Rimborsato",
                    color: "var(--sf-blue)"
                }
            case OrderStatus.WaitingForRefund:
                return {
                    label: "In attesa del rimborso",
                    color: "var(--sf-gray)"
                }
            case OrderStatus.PartiallyRefunded:
                return {
                    label: "Parzialmente rimborsato",
                    color: "var(--sf-orange)"
                }
            case OrderStatus.RefundFailed:
                return {
                    label: "Rimborso fallito",
                    color: "var(--sf-red)"
                }
            case OrderStatus.Unpaid:
                return {
                    label: "Non pagato",
                    color: "var(--sf-red)"
                }
            case OrderStatus.PartiallyPaid:
                return {
                    label: "Parzialmente pagato",
                    color: "var(--sf-orange)"
                }
            case OrderStatus.Abandoned:
                return {
                    label: "Non pagherà",
                    color: "black"
                }
            default: return {
                label: status || "Sconosciuto",
                color: "var(--sf-gray)"
            }
        }
    }

    const getInstallments = async () => {
        setLoading(true)
        try {

            const params = new URLSearchParams();

            params.append("page", page);
            params.append("per", per);

            params.append("status", "pending,requires_action,failed,abandoned,cancelled");

            if (q && q.trim().length > 0) {
                params.append("q", q);
            }

            const data = await api.get(`/admin/installments?${params.toString()}`);
            const { installments, count } = data;

            setInstallments(Array.isArray(installments) ? installments : []);
            setCount(count);
        }
        catch (error) {
            console.error(error);
        }
        setLoading(false)
    }

    useEffect(() => {
        getInstallments();
    }, [q, page, per])

    useEffect(() => {
        if (width < 540) {
            setPer(5);
        } else {
            setPer(10);
        }
    }, [width])

    const getInstallmentBadge = (installment) => {
        let badge = { ...getInstallmentStatus(installment?.status) }
        badge.name = badge.label
        if (installment?.status === OrderStatus.Abandoned) return badge;
        if (addDays(new Date(), 5) >= new Date(installment.expired_at) && ![OrderStatus.Succeeded, OrderStatus.Refunded, OrderStatus.PartiallyRefunded, OrderStatus.WaitingForRefund, OrderStatus.RefundFailed].includes(installment?.status)) {
            badge = { ...getInstallmentStatus(OrderStatus.PartiallyPaid) }
            badge.name = 'In scadenza'
        }
        if (new Date(installment.expired_at) < new Date() && ![OrderStatus.Succeeded, OrderStatus.Refunded, OrderStatus.PartiallyRefunded, OrderStatus.WaitingForRefund, OrderStatus.RefundFailed].includes(installment?.status)) {
            badge = { ...getInstallmentStatus(OrderStatus.Unpaid) }
            badge.name = 'Scaduta'
        }

        if (addDays(new Date(installment.expired_at), 5) < new Date() && ![OrderStatus.Succeeded, OrderStatus.Refunded, OrderStatus.PartiallyRefunded, OrderStatus.WaitingForRefund, OrderStatus.RefundFailed, OrderStatus.Abandoned].includes(installment?.status)) {
            badge = { ...getInstallmentStatus(OrderStatus.Unpaid) }
            badge.name = 'Moroso'
        }

        if (addDays(new Date(installment.expired_at), 5) < new Date() && [OrderStatus.Abandoned].includes(installment?.status)) {
            badge = { ...getInstallmentStatus(OrderStatus.Abandoned) }
            badge.name = 'Non pagherà'
        }

        return badge;
    }

    return (
        <Card appearance={modern ? "modern" : undefined} title="RATE IN SCADENZA" noPadding style={{ maxHeight: '750px' }}>
            <div className={styles.installments}>
                <div className={styles.searchToolbar}>
                    <TextInput type="search" appearance="modern" label="Cerca nelle rate" placeholder="Cerca nelle rate…" value={q} onKeyUp={v => setQ(v)} />
                </div>
                <div className={styles.installmentsList}>
                    <Table
                        appearance={modern ? "minimal" : undefined}
                        style={{ borderRadius: 0, maxHeight: '525px' }}
                        loading={loading}
                        loadingN={per}
                        columns={[
                            { key: "user", label: "UTENTE", type: "string", wrap: true },
                            { key: "email", label: "EMAIL", type: "string" },
                            { key: "amount", label: "IMPORTO", type: "custom" },
                            { key: "status", label: "STATO", type: "custom", style: { justifyContent: 'flex-start' } },
                            { key: "expired_at", label: "SCADENZA", type: "datetime", options: { day: 'numeric', month: 'short', year: 'numeric' }, forceNumeric: false },
                            { key: "order_no", label: "ORDINE", type: "custom", },
                        ]}
                        elements={installments.map((installment) => (
                            {
                                id: installment.id,
                                user: installment.order.user.name + ' ' + installment.order.user.surname,
                                email: installment.order.user.email,
                                amount: { value: installment.amount, type: "number", component: <div>€ {formatPrice(installment.amount / 100)}</div> },
                                status: { value: installment.status, type: "string", component: modern ? <StatusBadge appearance="soft" label={getInstallmentBadge(installment).name} color={getInstallmentBadge(installment).color} /> : <Badge {...getInstallmentBadge(installment)} size="small" /> },
                                expired_at: installment.expired_at,
                                order_no: {
                                    value: `#${installment.order.order_no}`,
                                    type: "string",
                                    component:
                                        <TooltipWrapper text={installment.order.products.map(p => p.name).join("\n")}>
                                            <div>{`#${installment.order.order_no}`}</div>
                                        </TooltipWrapper>
                                },
                                href: `/orders/${installment.order.id}`
                            }
                        ))}
                    />
                </div>

                {
                    showPagination &&
                    <div className={styles.paginationFooter}>
                        <Pagination total={count} per={per} page={page} group={width > 540 ? 5 : 0} onChange={p => setPage(p)} />
                    </div>
                }
            </div>
        </Card>
    )

}

export default InstallmentsCard;
